import { Component, Type } from "@wonderlandengine/api";

import { quat2, vec3 } from "gl-matrix";
import { storageSpace13 } from "./globals.js";

export class MeshParticles extends Component {
    static TypeName = "mesh-particles";
    static Properties = {
        /* Mesh for spawned particles */
        mesh: { type: Type.Mesh, default: null },
        /* Material for spawned particles */
        material: { type: Type.Material, default: null },
        /* Delay between particle spawns. If below time of a frame, will spawn multiple particles in update. */
        delay: { type: Type.Float, default: 0.1 },
        /* Maximum number of particles, once limit is reached, particles are recycled first-in-first-out. */
        maxParticles: { type: Type.Int, default: 64 },
        /* Initial speed of emitted particles. */
        initialSpeed: { type: Type.Float, default: 30 },
        /* Size of a particle */
        particleScale: { type: Type.Float, default: 0.1 },
        /* Spread of a particles, 0 to 1 */
        spread: { type: Type.Float, default: 0.5 },
        life: { type: Type.Float, default: 1 },
    };

    init() {
        this.time = 0.0;
        this.count = 0;
    }

    start() {
        this.objects = [];
        this.velocities = [];
        this.life = [];
        for (let i = 0; i < this.maxParticles; ++i) {
            this.velocities.push([0, 0, 0]);
            this.life.push(0);

            let obj = storageSpace13.particlePool.getItem();
            obj.name = "particle" + this.count.toString();
            let mesh = obj.getComponent("mesh");
            if (!mesh) {
                mesh = obj.addComponent("mesh");
                mesh.mesh = this.mesh;
                mesh.material = this.material;
            } else {
                mesh.active = true;
            }

            /* Most efficient way to hide the mesh */
            obj.scale([0, 0, 0]);
            this.objects.push(obj);
        }
    }

    update(dt) {
        this.time += dt;
        if (this.time > this.delay) {
            /* Time to spawn particles */
            const spawnCount = Math.floor(this.time / this.delay);
            for (let i = 0; i < spawnCount; ++i) this.spawn();
            this.time -= this.delay * spawnCount;
        }

        /* Target for retrieving particles world locations */
        let origin = [0, 0, 0];
        let distance = [0, 0, 0];
        for (let i = 0; i < Math.min(this.count, this.objects.length); ++i) {
            this.life[i] += dt;
            /* Get translation first, as object.translate() will mark
             * the object as dirty, which will cause it to recalculate
             * obj.transformWorld on access. We want to avoid this and
             * have it be recalculated in batch at the end of frame
             * instead */
            quat2.getTranslation(origin, this.objects[i].transformWorld);
        }

        for (let i = 0; i < Math.min(this.count, this.objects.length); ++i) {
            /* Apply velocity */
            vec3.scale(distance, this.velocities[i], dt);
            this.objects[i].translate(distance);
            let s = Math.sin((this.life[i] / 4) * Math.PI) / 15 + 0.02;
            this.objects[i].scalingWorld = [s, s, s];
        }
    }

    /** Spawn a particle */
    spawn() {
        let index = this.count % this.maxParticles;
        this.life[index] = 0;
        let obj = this.objects[index];

        obj.resetTransform();

        obj.scale([this.particleScale, this.particleScale, this.particleScale]);

        /* Activate component, otherwise it will not show up! */
        obj.getComponent("mesh").active = true;

        const origin = [];
        quat2.getTranslation(origin, this.object.transformWorld);
        obj.translate(origin);
        obj.translate([Math.random() * 0.5 - 0.25, 0, Math.random() * 0.5 - 0.25]);

        this.velocities[index][0] = 0; //(Math.random() - 0.5)*this.spread;
        this.velocities[index][1] = Math.random();
        this.velocities[index][2] = 0; //(Math.random() - 0.5)*this.spread;

        vec3.normalize(this.velocities[index], this.velocities[index]);
        vec3.scale(
            this.velocities[index],
            this.velocities[index],
            this.initialSpeed
        );

        this.count += 1;
    }
}
